import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button, FilterChip } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { useTranslation } from '../i18n';
import MobileSheet from './MobileSheet';

/**
 * The sticky chip strip, and the sheet the rest of the filters live in.
 *
 * A strip may scroll sideways; the PAGE may not. That distinction is the whole
 * design of this component — three or four Persian segment labels overflow any
 * `inline-flex` group at 320px, and a group with no overflow strategy takes the
 * page with it. So the chips sit in their own `overflow-x: auto` rail, and the
 * controls that do not fit as chips (a select, a date range, a sort order) move
 * into a `MobileSheet` behind one «فیلترها» chip carrying a count of how many
 * are active.
 *
 * A chip whose count the server could not compute is still RENDERED — removing
 * it would make the row jump as sides fail — but disabled, and on a phone the
 * reason is visible text under the strip rather than a `title` nobody can hover.
 */
export default function MobileFilterBar({
  /** `[{ id, label, count, active, disabled, disabledReason }]` */
  chips = [],
  onSelect,
  /** How many controls inside the sheet are currently non-default. */
  activeFilterCount = 0,
  /** Sheet contents. Omit to render the strip alone. */
  filterSheet,
  sticky = true,
  'data-testid': testId,
}) {
  const { t, formatNumber } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const reasons = chips.filter((c) => c.disabled && c.disabledReason);

  return (
    <>
      <div
        style={{
          ...(sticky ? { position: 'sticky', top: 0, zIndex: 2 } : null),
          background: C.bg,
          paddingBlock: '0.5rem',
        }}
        data-testid={testId}
      >
        <div
          role="group"
          className="mobile-strip"
          style={{
            display: 'flex', gap: '0.375rem', alignItems: 'center',
            overflowX: 'auto', paddingInline: '1rem',
          }}
        >
          {chips.map((c) => (
            <FilterChip
              key={c.id}
              label={c.label}
              count={c.count == null ? undefined : formatNumber(c.count)}
              active={c.active}
              disabled={c.disabled}
              onClick={() => onSelect?.(c.id)}
              data-testid={testId ? `${testId}-chip-${c.id}` : undefined}
              style={{ flexShrink: 0 }}
            />
          ))}

          {filterSheet && (
            <Button
              variant={activeFilterCount > 0 ? 'subtle' : 'outline'}
              size="sm"
              onClick={() => setOpen(true)}
              aria-expanded={open}
              data-testid={testId ? `${testId}-filters` : undefined}
              style={{ flexShrink: 0, minHeight: '2.75rem', gap: '0.375rem' }}
            >
              <SlidersHorizontal size={14} aria-hidden="true" />
              {t('common.actions.filters')}
              {activeFilterCount > 0 ? ` (${formatNumber(activeFilterCount)})` : ''}
            </Button>
          )}
        </div>

        {/* On a phone a disabled control has to say why in words: `title` is
            not an affordance without a pointer. */}
        {reasons.length > 0 && (
          <div style={{ paddingInline: '1rem', marginTop: '0.375rem' }}>
            {reasons.map((c) => (
              <p key={c.id} style={{ fontSize: '0.6875rem', color: C.textMuted, margin: 0 }}>
                {c.disabledReason}
              </p>
            ))}
          </div>
        )}
      </div>

      {filterSheet && (
        <MobileSheet
          open={open}
          onClose={() => setOpen(false)}
          title={t('common.actions.filters')}
          data-testid={testId ? `${testId}-filter-sheet` : undefined}
          footer={(
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                onClick={() => { filterSheet.onApply?.(); setOpen(false); }}
                style={{ flex: 1, minHeight: '2.75rem' }}
              >
                {t('common.actions.apply')}
              </Button>
              <Button
                variant="outline"
                onClick={() => { filterSheet.onClear?.(); setOpen(false); }}
                style={{ minHeight: '2.75rem' }}
              >
                {t('common.actions.clearFilters')}
              </Button>
            </div>
          )}
        >
          <div style={{ padding: '0.875rem 1rem', display: 'grid', gap: '0.875rem' }}>
            {filterSheet.children}
          </div>
        </MobileSheet>
      )}
    </>
  );
}
